import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, ArchiveIcon, RotateCcwIcon, LayersIcon, LockIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusCircleIcon, MinusCircleIcon, UserIcon, BriefcaseIcon, FilterIcon, StethoscopeIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Patient, SessionPackage } from '../../types';
import { formatDate } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Pagination } from '../ui/Pagination';
// Cuántas sesiones o menos restantes se consideran "por terminar" (alerta)
const LOW_SESSIONS_THRESHOLD = 2;
interface PackageRow {
  patient: Patient;
  pkg: SessionPackage;
}
export function SessionPackages() {
  const {
    patients,
    services,
    getService,
    getPatient,
    doctors,
    getDoctor,
    addSessionPackage,
    updateSessionPackage,
    archiveSessionPackage,
    unarchiveSessionPackage,
    registerSessionUsage,
    undoSessionUsage
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('sessionPackages');
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  // Si el usuario logueado es un doctor (no admin), solo ve sus propios paquetes asignados
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctorUser = !!currentUserDoctor && !isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  // Debounce: solo recalculamos los filtros 250ms después de que el usuario deja de escribir,
  // así escribir en el buscador no se siente lento al recorrer la lista en cada tecla.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => clearTimeout(timeout);
  }, [searchTerm]);
  const [serviceFilter, setServiceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'activo' | 'completado' | 'cancelado' | 'por_terminar' | 'archivado'>('todos');
  // Form state (assign / edit package)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PackageRow | null>(null);
  const [formData, setFormData] = useState({
    patientId: '',
    serviceId: '',
    doctorId: '',
    totalSessions: 6,
    notes: '',
    discountPercent: 15
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Detail modal (history + manual session registration)
  const [detailRow, setDetailRow] = useState<PackageRow | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  // Archive confirmation
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [rowToArchive, setRowToArchive] = useState<PackageRow | null>(null);
  // Flatten all patients' session packages into rows for the table/grid
  const allRows: PackageRow[] = useMemo(() => {
    const rows: PackageRow[] = [];
    patients.forEach(patient => {
      ;
      (patient.sessionPackages || []).forEach(pkg => {
        rows.push({
          patient,
          pkg
        });
      });
    });
    // Most recently created first
    return rows.sort((a, b) => new Date(b.pkg.createdAt).getTime() - new Date(a.pkg.createdAt).getTime());
  }, [patients]);
  // El doctor solo ve los paquetes que tiene asignados a él mismo
  const visibleRows = useMemo(() => isDoctorUser && currentUserDoctor ? allRows.filter(r => r.pkg.doctorId === currentUserDoctor.id) : allRows, [allRows, isDoctorUser, currentUserDoctor]);
  const filteredRows = useMemo(() => {
    const term = debouncedSearchTerm.toLowerCase();
    return visibleRows.filter(({
      patient,
      pkg
    }) => {
      const service = getService(pkg.serviceId);
      const matchesSearch = !term || patient.name.toLowerCase().includes(term) || (service?.name || '').toLowerCase().includes(term);
      const matchesService = !serviceFilter || pkg.serviceId === serviceFilter;
      const remaining = pkg.totalSessions - pkg.usedSessions;
      const isLow = pkg.status === 'activo' && remaining > 0 && remaining <= LOW_SESSIONS_THRESHOLD;
      let matchesStatus = true;
      if (statusFilter === 'por_terminar') matchesStatus = isLow;else if (statusFilter === 'todos') matchesStatus = pkg.status !== 'archivado';else matchesStatus = pkg.status === statusFilter;
      return matchesSearch && matchesService && matchesStatus;
    });
  }, [visibleRows, debouncedSearchTerm, serviceFilter, statusFilter, getService]);
  const {
    paginatedItems: paginatedRows,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredRows);
  // KPIs for header banner
  const activePackages = useMemo(() => visibleRows.filter(r => r.pkg.status === 'activo'), [visibleRows]);
  const completedPackages = useMemo(() => visibleRows.filter(r => r.pkg.status === 'completado'), [visibleRows]);
  const archivedPackages = useMemo(() => visibleRows.filter(r => r.pkg.status === 'archivado'), [visibleRows]);
  const lowSessionPackages = useMemo(() => activePackages.filter(r => {
    const remaining = r.pkg.totalSessions - r.pkg.usedSessions;
    return remaining > 0 && remaining <= LOW_SESSIONS_THRESHOLD;
  }), [activePackages]);
  const patientsWithPackages = useMemo(() => new Set(visibleRows.map(r => r.patient.id)).size, [visibleRows]);
  const serviceOptions = useMemo(() => services.map(s => ({
    value: s.id,
    label: s.name
  })), [services]);
  const serviceFilterOptions = useMemo(() => [{
    value: '',
    label: 'Todos los servicios'
  }, ...serviceOptions], [serviceOptions]);
  const patientOptions = useMemo(() => patients.map(p => ({
    value: p.id,
    label: p.name
  })), [patients]);
  const doctorOptions = useMemo(() => doctors.map(d => ({
    value: d.id,
    label: d.name
  })), [doctors]);
  // ===== Handlers =====
  const openNewForm = () => {
    if (!canCreate('sessionPackages')) return;
    setEditingRow(null);
    setFormData({
      patientId: '',
      serviceId: '',
      doctorId: isDoctorUser && currentUserDoctor ? currentUserDoctor.id : '',
      totalSessions: 6,
      notes: '',
      discountPercent: 15
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (row: PackageRow) => {
    if (!canEdit('sessionPackages')) return;
    setEditingRow(row);
    setFormData({
      patientId: row.patient.id,
      serviceId: row.pkg.serviceId,
      doctorId: row.pkg.doctorId || '',
      totalSessions: row.pkg.totalSessions,
      notes: row.pkg.notes || '',
      discountPercent: row.pkg.discountPercent ?? 15
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!editingRow && !formData.patientId) errors.patientId = 'Selecciona un paciente';
    if (!editingRow && !formData.serviceId) errors.serviceId = 'Selecciona un servicio';
    if (!formData.totalSessions || formData.totalSessions < 1) errors.totalSessions = 'Ingresa una cantidad válida de sesiones';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: editingRow ? 'Guardar Cambios' : 'Asignar Paquete',
      message: editingRow ? '¿Estás seguro de que deseas guardar los cambios de este paquete de sesiones?' : '¿Estás seguro de que deseas asignar este paquete de sesiones al paciente?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          if (editingRow) {
            await updateSessionPackage(editingRow.patient.id, editingRow.pkg.id, {
              totalSessions: formData.totalSessions,
              notes: formData.notes,
              doctorId: formData.doctorId || undefined,
              discountPercent: formData.discountPercent
            });
            showToast({
              type: 'success',
              title: 'Paquete actualizado',
              message: 'Los cambios fueron guardados y registrados en la bitácora.'
            });
          } else {
            await addSessionPackage(formData.patientId, {
              serviceId: formData.serviceId,
              doctorId: formData.doctorId || undefined,
              totalSessions: formData.totalSessions,
              notes: formData.notes,
              discountPercent: formData.discountPercent
            });
            showToast({
              type: 'success',
              title: 'Paquete asignado',
              message: 'El servicio fue asignado al paciente.'
            });
          }
          setIsFormOpen(false);
        } catch (err) {
          logger.error('Error guardando paquete de sesiones:', err);
          showToast({
            type: 'error',
            title: 'Error',
            message: 'No se pudo guardar el paquete de sesiones.'
          });
        }
      }
    });
  };
  const openDetail = (row: PackageRow) => {
    setDetailRow(row);
    setIsDetailOpen(true);
  };
  // Keep detail modal data fresh after mutations (re-pull from patients list)
  const refreshDetail = (patientId: string, packageId: string) => {
    const refreshedPatient = getPatient(patientId);
    const refreshedPkg = refreshedPatient?.sessionPackages?.find(p => p.id === packageId);
    if (refreshedPatient && refreshedPkg) {
      setDetailRow({
        patient: refreshedPatient,
        pkg: refreshedPkg
      });
    }
  };
  const handleRegisterSession = async (row: PackageRow) => {
    try {
      await registerSessionUsage(row.patient.id, row.pkg.id);
      refreshDetail(row.patient.id, row.pkg.id);
      showToast({
        type: 'success',
        title: 'Sesión registrada',
        message: 'Se sumó 1 sesión al paquete.'
      });
    } catch (err) {
      logger.error('Error registrando sesión:', err);
    }
  };
  const handleUndoSession = async (row: PackageRow, logEntryId: string) => {
    try {
      await undoSessionUsage(row.patient.id, row.pkg.id, logEntryId);
      refreshDetail(row.patient.id, row.pkg.id);
    } catch (err) {
      logger.error('Error revirtiendo sesión:', err);
    }
  };
  const confirmArchive = (row: PackageRow) => {
    if (!canDelete('sessionPackages')) return;
    confirm({
      title: 'Archivar Paquete de Sesiones',
      message: `¿Deseas archivar el paquete de sesiones de "${row.patient.name}"? El paquete dejará de aparecer en la vista activa, pero su historial se conserva y puede restaurarse en cualquier momento desde la pestaña "Archivados".`,
      confirmText: 'Archivar',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await archiveSessionPackage(row.patient.id, row.pkg.id);
          showToast({
            type: 'success',
            title: 'Paquete archivado',
            message: 'El paquete fue archivado. Su historial se conserva y queda registrado en la bitácora.'
          });
          setIsDetailOpen(false);
        } catch (err) {
          logger.error('Error archivando paquete:', err);
        }
      }
    });
  };
  const handleUnarchive = async (row: PackageRow) => {
    try {
      await unarchiveSessionPackage(row.patient.id, row.pkg.id);
      refreshDetail(row.patient.id, row.pkg.id);
      showToast({
        type: 'success',
        title: 'Paquete restaurado',
        message: 'El paquete fue restaurado desde archivados.'
      });
    } catch (err) {
      logger.error('Error restaurando paquete:', err);
    }
  };
  return <div className="space-y-6" data-id="element-5806" data-mp="22vt3am4nofpg">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600 via-cyan-500 to-blue-500 shadow-xl text-white" data-id="element-5807" data-mp="2guizlzjnibcr">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px), repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-5808" data-mp="2bxitjomsx8ln" />
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-5809" data-mp="2qgtp4dsqr4ih" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-5810" data-mp="2cpf7obehfbdj" />
        <div className="relative z-10 p-6 pl-7" data-id="element-5811" data-mp="2hk2s9z6d7lmu">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-5812" data-mp="22u525dovklv0">
            <div data-id="element-5813" data-mp="27t89j4sbv465">
              <div className="flex items-center gap-3 mb-2" data-id="element-5814" data-mp="27soovgbam1of">
                <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-5815" data-mp="1trvucvkzr6lb">
                  <LayersIcon className="w-6 h-6 text-white" data-id="element-5816" data-mp="1xu8xdc8csqee" />
                </div>
                <h1 className="text-2xl font-bold text-white" data-id="element-5817" data-mp="1qqj6ryhcj057">
                  Paquetes de Sesiones
                </h1>
              </div>
              <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-5818" data-mp="1lt0zeciz8szb">
                Servicios por cantidad de sesiones asignados a pacientes (ej:
                láser). Lleva el control de cuántas sesiones ha usado cada uno.
                {isDoctorUser && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-5819" data-mp="180uj8qftqlzv">
                    Viendo solo tus paquetes asignados
                  </span>}
                {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-5820" data-mp="y2wsnwahsd43">
                    Solo lectura
                  </span>}
              </p>
            </div>
            <div className="flex gap-3" data-id="element-5821" data-mp="2sev5s9bx9t59">
              {canCreate('sessionPackages') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-5823" data-mp="2neywbukgh9ib" />} onClick={openNewForm} className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-lg shadow-cyan-900/20" data-id="element-5822" data-mp="2nid1faki7wtg">
                  Asignar Paquete
                </Button>}
            </div>
          </div>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-5824" data-mp="39q0a54r7x1ub">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-5825" data-mp="39rbfva484jo2">
              <p className="text-xs text-white/70 font-medium" data-id="element-5826" data-mp="2d1kkz9s0suhq">
                Pacientes con paquetes
              </p>
              <p className="text-xl font-bold text-white" data-id="element-5827" data-mp="2m060ew273tea">
                {patientsWithPackages}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-5828" data-mp="2r00o6qqoc2v4">
              <p className="text-xs text-white/70 font-medium" data-id="element-5829" data-mp="18m2795vcnjm">
                Paquetes activos
              </p>
              <p className="text-xl font-bold text-white" data-id="element-5830" data-mp="6770h31k8x5f">
                {activePackages.length}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-5831" data-mp="3crtrgotimmvr">
              <p className="text-xs text-white/70 font-medium" data-id="element-5832" data-mp="37ta0ga02ekwj">Completados</p>
              <p className="text-xl font-bold text-white" data-id="element-5833" data-mp="3mnewywxsm5a9">
                {completedPackages.length}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-5834" data-mp="2tz9c8a6qogaf">
              <p className="text-xs text-white/70 font-medium flex items-center gap-1" data-id="element-5835" data-mp="2p0oag6bu9ojv">
                <AlertTriangleIcon className="w-3 h-3" data-id="element-5836" data-mp="33pc241ikde4j" /> Por terminar
              </p>
              <p className="text-xl font-bold text-white" data-id="element-5837" data-mp="33r5bhokkacg8">
                {lowSessionPackages.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-5838" data-mp="2b6olozeiin9s">
          <LockIcon className="w-4 h-4" data-id="element-5839" data-mp="lpt339llahkp" />
          <span data-id="element-5840" data-mp="qj4e7mxfpkif">
            Solo tienes permisos de lectura. No puedes asignar, editar o
            archivar paquetes.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-3 md:items-end" data-id="element-5841" data-mp="gsjg00loai01">
        <div className="w-full md:w-80 relative" data-id="element-5842" data-mp="gqqawpqpk5u5">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5843" data-mp="14i3y6ha05b89" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar por paciente o servicio..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-5844" data-mp="19gyhrlfsn3tf" />
        </div>
        <div className="w-full md:w-64" data-id="element-5845" data-mp="3k99t0zfo9vb8">
          <Select options={serviceFilterOptions} value={serviceFilter} onChange={setServiceFilter} placeholder="Filtrar por servicio" data-id="element-5846" data-mp="3kab2x0kycbrd" />
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-5847" data-mp="ar2enlrosr9r">
          {[{
          key: 'todos',
          label: 'Todos'
        }, {
          key: 'activo',
          label: 'Activos'
        }, {
          key: 'por_terminar',
          label: 'Por terminar'
        }, {
          key: 'completado',
          label: 'Completados'
        }, {
          key: 'cancelado',
          label: 'Cancelados'
        }, {
          key: 'archivado',
          label: 'Archivados'
        }].map(tab => <button key={tab.key} onClick={() => setStatusFilter(tab.key as any)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${statusFilter === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-5848" data-mp="1dfh0o0rlarvu">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5849" data-mp="23og249ch4y94">
        {paginatedRows.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-5850" data-mp="28mzp8d2t0g32">
            <div className="flex flex-col items-center justify-center" data-id="element-5851" data-mp="2dglrrcf973l5">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-5852" data-mp="2ienatxcoj2wm">
                <LayersIcon className="w-8 h-8 text-gray-400" data-id="element-5853" data-mp="2mha5s74nd3y1" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-5854" data-mp="2reaaoeprg0nk">
                No se encontraron paquetes
              </h3>
              <p className="text-sm" data-id="element-5855" data-mp="3sdqxp7zj65q5">
                {searchTerm || serviceFilter || statusFilter !== 'todos' ? 'Intenta con otros filtros de búsqueda' : 'Asigna un servicio con sesiones a un paciente para comenzar'}
              </p>
            </div>
          </div> : paginatedRows.map(({
        patient,
        pkg
      }) => {
        const service = getService(pkg.serviceId);
        const doctor = pkg.doctorId ? getDoctor(pkg.doctorId) : undefined;
        const progress = pkg.totalSessions > 0 ? Math.min(100, Math.round(pkg.usedSessions / pkg.totalSessions * 100)) : 0;
        const isComplete = pkg.usedSessions >= pkg.totalSessions;
        const isArchived = pkg.status === 'archivado';
        const remaining = pkg.totalSessions - pkg.usedSessions;
        const isLow = pkg.status === 'activo' && remaining > 0 && remaining <= LOW_SESSIONS_THRESHOLD;
        return <div key={pkg.id} className={`group relative flex flex-col bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-200/80 dark:ring-gray-700/60 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden cursor-pointer ${isArchived ? 'opacity-70' : ''}`} onClick={() => openDetail({
          patient,
          pkg
        })} data-id="element-5856" data-mp="181x0w34gj0je">
                <div className={`h-1 ${isArchived ? 'bg-gray-300' : isComplete ? 'bg-gradient-to-r from-emerald-500 to-green-400' : isLow ? 'bg-gradient-to-r from-amber-500 to-yellow-400' : 'bg-gradient-to-r from-cyan-500 to-blue-400'}`} data-id="element-5857" data-mp="1lwz60o1hclv5" />
                <div className="p-5 flex-1" data-id="element-5858" data-mp="29nmqe8sl13yl">
                  <div className="flex items-start justify-between gap-2 mb-3" data-id="element-5859" data-mp="7quaig82icc3">
                    <div className="min-w-0" data-id="element-5860" data-mp="cqgynkpfpczo">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide mb-0.5" data-id="element-5861" data-mp="cp5kcqyd4mub">
                        <UserIcon className="w-3 h-3" data-id="element-5862" data-mp="30np0p8ur7y2" /> Paciente
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug truncate" data-id="element-5863" data-mp="2yaxplh7fl8c">
                        {patient.name}
                      </h3>
                    </div>
                    <Badge variant={isArchived ? 'default' : isComplete ? 'success' : pkg.status === 'cancelado' ? 'danger' : isLow ? 'warning' : 'info'} data-id="element-5864" data-mp="3q30mnyhi8bqj">
                      {isArchived ? 'Archivado' : isComplete ? 'Completado' : pkg.status === 'cancelado' ? 'Cancelado' : isLow ? 'Por terminar' : 'En curso'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 mb-1.5" data-id="element-5865" data-mp="3dmrgkky1gmn1">
                    <BriefcaseIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-5866" data-mp="3rma9i6ia3rer" />
                    {service?.name || 'Servicio eliminado'}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-1.5" data-id="element-5867" data-mp="3mo8qtmorlu3t">
                    <StethoscopeIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-5868" data-mp="1xaluk4c1roud" />
                    {doctor ? doctor.name : 'Sin doctor asignado'}
                  </div>
                  {!!pkg.discountPercent && pkg.status === 'activo' && <div className="mb-4" data-id="element-5869" data-mp="1yp57w0ble02q">
                      <span className="inline-flex items-center text-[11px] font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded-full" data-id="element-5870" data-mp="1yntx3gjqbs24">
                        -{pkg.discountPercent}% por paquete
                      </span>
                    </div>}
                  {(!pkg.discountPercent || pkg.status !== 'activo') && <div className="mb-4" data-id="element-5871" data-mp="1fzgkqtdzfnxx" />}
                  {/* Progress */}
                  <div data-id="element-5872" data-mp="1kwyuglztq76z">
                    <div className="flex items-center justify-between mb-1" data-id="element-5873" data-mp="1kwff9d1k5wwn">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300" data-id="element-5874" data-mp="1pqb7rzz1yrkf">
                        {pkg.usedSessions} de {pkg.totalSessions} sesiones
                      </span>
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400" data-id="element-5875" data-mp="x6uow932rxdc">
                        {progress}%
                      </span>
                    </div>
                    <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5876" data-mp="36hvgei2ywskz">
                      <div className={`h-full rounded-full transition-all ${isArchived ? 'bg-gray-400' : isComplete ? 'bg-emerald-500' : isLow ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                  width: `${progress}%`
                }} data-id="element-5877" data-mp="31gfy2xs6d06a"></div>
                    </div>
                  </div>
                </div>
                {/* Footer actions */}
                <div className="px-5 pb-4 flex items-center justify-between border-t border-gray-100 dark:border-gray-700/60 pt-3" onClick={e => e.stopPropagation()} data-id="element-5878" data-mp="2dt80z34wgxfc">
                  <button onClick={() => openDetail({
              patient,
              pkg
            })} className="text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:underline" data-id="element-5879" data-mp="23vtmva6g8kq7">
                    Ver detalle
                  </button>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-150" data-id="element-5880" data-mp="1p71ri7od5k76">
                    {!readOnly && canEdit('sessionPackages') && !isArchived && <button onClick={() => openEditForm({
                patient,
                pkg
              })} className="p-1.5 text-gray-400 hover:text-cyan-600 rounded-lg transition-colors" title="Editar" data-id="element-5881" data-mp="2hwqrqit91g7q">
                        <EditIcon className="w-4 h-4" data-id="element-5882" data-mp="30n8gkcaynrnt" />
                      </button>}
                    {!readOnly && canDelete('sessionPackages') && !isArchived && <button onClick={() => confirmArchive({
                patient,
                pkg
              })} className="p-1.5 text-gray-400 hover:text-amber-600 rounded-lg transition-colors" title="Archivar" data-id="element-5883" data-mp="k8mzaj3y78e7">
                          <ArchiveIcon className="w-4 h-4" data-id="element-5884" data-mp="g7bin331xxi5" />
                        </button>}
                    {!readOnly && canEdit('sessionPackages') && isArchived && <button onClick={() => handleUnarchive({
                patient,
                pkg
              })} className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg transition-colors" title="Restaurar" data-id="element-5885" data-mp="1rmgky5z9tu18">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-5886" data-mp="3q4k12bmvywjc" />
                      </button>}
                  </div>
                </div>
              </div>;
      })}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-5887" data-mp="3ph3yw0fsoxs" />

      {/* Form Modal: Assign / Edit Package */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={editingRow ? 'Editar Paquete de Sesiones' : 'Asignar Paquete de Sesiones'} size="md" icon={<LayersIcon className="w-5 h-5" data-id="element-5889" data-mp="2jhoho4td6zt1" />} accentColor="blue" data-id="element-5888" data-mp="hn5scdtpqe7l">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-5890" data-mp="2bxngzcyanpji">
          {!editingRow && <>
              <Select label="Paciente" options={patientOptions} value={formData.patientId} onChange={v => setFormData({
            ...formData,
            patientId: v
          })} error={formErrors.patientId} placeholder="Selecciona un paciente" searchable data-id="element-5891" data-mp="273j8uqdalbgu" />
              <Select label="Servicio" options={serviceOptions} value={formData.serviceId} onChange={v => setFormData({
            ...formData,
            serviceId: v
          })} error={formErrors.serviceId} placeholder="Selecciona un servicio (ej: Láser facial)" searchable data-id="element-5892" data-mp="2kvzdfvludhjv" />
            </>}
          {editingRow && <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400 space-y-1" data-id="element-5893" data-mp="3tyz5qnl5ui65">
              <p data-id="element-5894" data-mp="cir171mhddhw">
                Paciente:{' '}
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-5895" data-mp="7p69frbhdfr5">
                  {editingRow.patient.name}
                </span>
              </p>
              <p data-id="element-5896" data-mp="gijbbirivrpd">
                Servicio:{' '}
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-5897" data-mp="ghq46gonj06p">
                  {getService(editingRow.pkg.serviceId)?.name}
                </span>
              </p>
            </div>}
          <Select label="Doctor asignado" options={doctorOptions} value={formData.doctorId} onChange={v => setFormData({
          ...formData,
          doctorId: v
        })} placeholder="Selecciona un doctor (opcional)" searchable disabled={isDoctorUser} data-id="element-5898" data-mp="qh8iohlsltrd" />
          <Input type="number" label="Cantidad total de sesiones" value={formData.totalSessions} onChange={e => setFormData({
          ...formData,
          totalSessions: parseInt(e.target.value) || 0
        })} error={formErrors.totalSessions} min={1} placeholder="Ej: 6" data-id="element-5899" data-mp="1cob8tyv4f10l" />
          <Input type="number" label="% de descuento del paquete" value={formData.discountPercent} onChange={e => setFormData({
          ...formData,
          discountPercent: parseInt(e.target.value) || 0
        })} error={formErrors.discountPercent} min={0} max={100} placeholder="Ej: 15" data-id="element-5900" data-mp="sysgmu35zcrt" />
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2" data-id="element-5901" data-mp="2cyc8ya1xaw5l">
            Este descuento se aplicará automáticamente al precio del servicio
            cada vez que el paciente use una sesión de este paquete, hasta que
            el paquete se complete, cancele o archive.
          </p>
          <Textarea label="Notas (opcional)" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={2} placeholder="Ej: Paquete promocional, no requiere todas las sesiones..." data-id="element-5902" data-mp="1y13e36deva94" />
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-5903" data-mp="2gz5y16r3qh02">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-5904" data-mp="3eethsc0mbwi8">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-5905" data-mp="3hwhd4gkutavi">
              {editingRow ? 'Guardar Cambios' : 'Asignar Paquete'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal: progress + history + manual session */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Detalle del Paquete" size="md" icon={<LayersIcon className="w-5 h-5" data-id="element-5907" data-mp="4pny4s1r999u" />} accentColor="blue" data-id="element-5906" data-mp="3qw4dcz1ukamz">
        {detailRow && (() => {
        const {
          patient,
          pkg
        } = detailRow;
        const service = getService(pkg.serviceId);
        const doctor = pkg.doctorId ? getDoctor(pkg.doctorId) : undefined;
        const progress = pkg.totalSessions > 0 ? Math.min(100, Math.round(pkg.usedSessions / pkg.totalSessions * 100)) : 0;
        const isComplete = pkg.usedSessions >= pkg.totalSessions;
        const isArchived = pkg.status === 'archivado';
        return <div className="space-y-4" data-id="element-5908" data-mp="1p4anudl3cwqh">
                <div className="flex items-center justify-between" data-id="element-5909" data-mp="1k8c3d12vvyvc">
                  <div data-id="element-5910" data-mp="1ywgoebpesmha">
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-5911" data-mp="1ywq5tawe1lbd">
                      {patient.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5912" data-mp="1ty5czwjgu3wy">
                      {service?.name || 'Servicio eliminado'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-5913" data-mp="119q4lsuuajul">
                      <StethoscopeIcon className="w-3 h-3" data-id="element-5914" data-mp="1f98zvl2jy0ph" />{' '}
                      {doctor ? doctor.name : 'Sin doctor asignado'}
                    </p>
                  </div>
                  <Badge variant={isArchived ? 'default' : isComplete ? 'success' : pkg.status === 'cancelado' ? 'danger' : 'info'} data-id="element-5915" data-mp="2qmux7tikf8m3">
                    {isArchived ? 'Archivado' : isComplete ? 'Completado' : pkg.status === 'cancelado' ? 'Cancelado' : 'En curso'}
                  </Badge>
                </div>
                {pkg.notes && <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-5916" data-mp="3huledwg4gwpj">
                    {pkg.notes}
                  </p>}
                <div data-id="element-5917" data-mp="3851q2b9n4ana">
                  <div className="flex items-center justify-between mb-1" data-id="element-5918" data-mp="3849x787fgr0p">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5919" data-mp="3vv5swgf086d8">
                      {pkg.usedSessions} de {pkg.totalSessions} sesiones
                    </span>
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400" data-id="element-5920" data-mp="ebyoi0lygqaa">
                      {progress}%
                    </span>
                  </div>
                  <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5921" data-mp="9bay9h5jwdh4">
                    <div className={`h-full rounded-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{
                width: `${progress}%`
              }} data-id="element-5922" data-mp="cfsg66f55ec"></div>
                  </div>
                </div>
                {!readOnly && canEdit('sessionPackages') && pkg.status !== 'cancelado' && <Button variant="outline" size="sm" disabled={isComplete} onClick={() => handleRegisterSession(detailRow)} data-id="element-5923" data-mp="3dsn6380tc6en">
                      <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-5924" data-mp="33vihl1jhvyuo" />
                      {isComplete ? 'Sesiones completas' : 'Registrar sesión manual'}
                    </Button>}
                <div data-id="element-5925" data-mp="1t91lvnd9bmpj">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-5926" data-mp="1t9t5dztkxtzg">
                    Historial ({pkg.log.length})
                  </h4>
                  {pkg.log.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-5927" data-mp="10mgsyih4v2jv">
                      Aún no hay sesiones registradas
                    </p> : <div className="space-y-2 max-h-56 overflow-y-auto" data-id="element-5928" data-mp="19k9a7s6f0k9w">
                      {pkg.log.slice().reverse().map(entry => <div key={entry.id} className="flex items-center justify-between gap-3 text-sm bg-gray-50 dark:bg-gray-900/30 rounded-lg px-3 py-2" data-id="element-5929" data-mp="htmoiih4ystw">
                            <div className="min-w-0" data-id="element-5930" data-mp="36eq86q5van7m">
                              <p className="text-gray-700 dark:text-gray-300" data-id="element-5931" data-mp="36g9m4a41wm68">
                                {formatDate(entry.date)}
                                {entry.appointmentId ? ' · cita completada' : ' · registro manual'}
                              </p>
                              {entry.registeredBy && <p className="text-xs text-gray-400" data-id="element-5932" data-mp="3pcsxz768oo6h">
                                  por {entry.registeredBy}
                                </p>}
                            </div>
                            {!readOnly && canEdit('sessionPackages') && <button type="button" title="Deshacer esta sesión" onClick={() => handleUndoSession(detailRow, entry.id)} className="text-gray-400 hover:text-red-500 flex-shrink-0" data-id="element-5933" data-mp="gyam0ud6gohl">
                                <MinusCircleIcon className="w-4 h-4" data-id="element-5934" data-mp="mlw4dxrtowc4" />
                              </button>}
                          </div>)}
                    </div>}
                </div>
                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-5935" data-mp="hlqeon545jwo">
                  {!readOnly && canEdit('sessionPackages') && !isArchived && <Button variant="outline" onClick={() => {
              setIsDetailOpen(false);
              openEditForm(detailRow);
            }} data-id="element-5936" data-mp="7vorryohy7jc">
                      <EditIcon className="w-4 h-4 mr-1" data-id="element-5937" data-mp="pvfsrun1zcun" /> Editar
                    </Button>}
                  {!readOnly && canDelete('sessionPackages') && !isArchived && <Button variant="danger" onClick={() => confirmArchive(detailRow)} data-id="element-5938" data-mp="g5mhyvuqj1rg">
                      <ArchiveIcon className="w-4 h-4 mr-1" data-id="element-5939" data-mp="l3ek0ip3g49d" /> Archivar
                    </Button>}
                  {!readOnly && canEdit('sessionPackages') && isArchived && <Button variant="outline" onClick={() => handleUnarchive(detailRow)} data-id="element-5940" data-mp="b7b6kgoskj74">
                      <RotateCcwIcon className="w-4 h-4 mr-1" data-id="element-5941" data-mp="25lhvimanyd1" /> Restaurar
                    </Button>}
                </div>
              </div>;
      })()}
      </Modal>
    </div>;
}